/* eslint-disable @typescript-eslint/no-floating-promises */
/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React from 'react';

import type {
  GfaLocaleService,
  GfaServiceConfigProvider,
  NotificationDisplayService,
  NotificationOptions,
  TrackingService,
} from './feature-service-types';

import { NotificationType } from './feature-service-types';

import { debug, isDebugMode } from './utils/debugging';
import { initTracking } from './utils/tracking';
import { createNotificationContent } from './utils/createNotificationContent';
import { createInitialState } from './utils/createInitialState';
import type { FeatureAppState } from './types';

interface FeatureAppProps {
  readonly localeService: GfaLocaleService;
  readonly serviceConfigProvider: GfaServiceConfigProvider;
  readonly notificationDisplayService?: NotificationDisplayService;
  readonly trackingService?: TrackingService;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  localeService,
  serviceConfigProvider,
  notificationDisplayService,
  trackingService,
}: FeatureAppProps) => {
  const [featureAppState, setFeatureAppState] = React.useState<FeatureAppState | undefined>(
    undefined,
  );

  const tracking = initTracking(
    {
      implementer: 37,
    },
    trackingService,
  );

  const onContentLinkClicked = () => {
    tracking.sendClickEvent();
  };

  React.useEffect(() => {
    tracking.sendReadyEvent();
    /* eslint-disable-next-line react-hooks/exhaustive-deps --
     * We skip the rule here because we just want to run the code once and not define a dependency
     * here. Omitting the entire dependency array or adding the `tracking` object as dependency
     * would run the code more than once, causing issues with registering impression tracking event.
     **/
  }, []);

  function openNotification() {
    if (!notificationDisplayService) {
      debug('notificationDisplayService not available!');
      return;
    }

    const type = NotificationType.POPUP_GENERIC;
    const id = `fa-social-media-forwarding-notice_notification`;
    const options: NotificationOptions = { onClose: onContentLinkClicked };
    const content = createNotificationContent(featureAppState, onContentLinkClicked);

    tracking.sendImpressionEvent();
    notificationDisplayService.createNotification(type, id, content as unknown as string, options); // content casted as string because we know that JSX Elements actually are supported

    debug(`Notification created! (type=${type}, id=${id})`);
  }

  React.useEffect(() => {
    const initialState = createInitialState(localeService, serviceConfigProvider);
    initialState.then((updatedState) => {
      setFeatureAppState(updatedState);
    });
  }, [localeService, serviceConfigProvider]);

  React.useEffect(() => {
    if (
      featureAppState &&
      featureAppState.redirectTagPath &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (featureAppState.translations || featureAppState.fallbackTranslations)
    ) {
      openNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureAppState]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (featureAppState && featureAppState.translations && isDebugMode()) {
    return (
      <div style={{ width: '360px', padding: '30px' }}>
        <p>Social Media Forwarding Notice debug output:</p>
        <div style={{ padding: '20px', backgroundColor: '#eee' }}>
          {createNotificationContent(featureAppState, onContentLinkClicked)}
        </div>
        <div style={{ padding: '20px' }}>
          <pre>{JSON.stringify(featureAppState, null, 2)}</pre>
        </div>
      </div>
    );
  }

  return null;
};

export default FeatureApp;
